import FnoTermDropDown from "@/components/Markets/Fno/FnoTermDropDown";
import OnHoverOption from "@/components/PaidUsers/OnHoverOption";
import { WebSocketProvider, WebSocketSample } from "@/contexts/WebSocket";
import Actions from "@/elements/Actions";
import axiosInterceptorInstance from "@/elements/axiosInterceptorInstance";
import LivePriceBox from "@/elements/LivePriceBox/LivePriceBox";
import { Mixpanel } from "@/elements/Mixpanel";
import NseOrBsePrice from "@/elements/NseOrBsePrice/NseOrBsePrice";
import { getInstrument } from "@/lib/tradeCards";
import { cn, putComma, scrollInside } from "@/lib/utils";
import moment from "moment";
import { useRouter } from "next/router";
import React, { Fragment, memo, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useSubscription } from "react-stomp-hooks";

function findClosestStock(data) {
    if (data?.CE && data?.PE) {
        const SpotP = data?.SpotP;
        const options = [...data?.CE, ...data?.PE];
        let closestOption = null;
        let smallestDiff = Infinity;
        options.forEach((option) => {
            const diff = Math.abs(option.Stk - SpotP);
            if (diff < smallestDiff && option.Stk >= SpotP) {
                smallestDiff = diff;
                closestOption = option;
            }
        });
        return closestOption;
    }
    return null;
}

function findMaxVolume(data) {
    if (data?.CE && data?.PE) {
        const options = [...data?.CE, ...data?.PE];
        let maxVolume = 0;
        options.forEach((option) => {
            if (option.Vol > maxVolume) {
                maxVolume = option.Vol;
            }
        });
        return maxVolume;
    }
    return null;
}

export default function OptionChain({ name, scriptData, source }) {
    const [stockDetails, setStockDetails] = useState({});
    const [expiryDate, setExpiryDate] = useState(null);
    const [data, setData] = useState([]);
    const [isLoading, setLoading] = useState(true);
    const [refresh, setRefresh] = useState(0);
    const [spotPrice, setSpotPrice] = useState(0);

    useEffect(() => {
        if (expiryDate) {
            const keysArray = Object.keys(stockDetails?.expiryDates);
            Mixpanel.pageView({
                page: "option_chain",
                symbol: stockDetails?.nseSymbol || stockDetails?.bseSymbol,
                expiry_date: expiryDate,
                instrument_name: getInstrument(stockDetails?.nseSymbol || stockDetails?.bseSymbol),
                expiry: "t" + (keysArray.indexOf(expiryDate) == 0 ? "" : "+" + keysArray.indexOf(expiryDate)),
                source: source
            });
        }
    }, [expiryDate]);

    useEffect(() => {
        let time;
        if (moment().format("H") >= 9 && moment().format("H") <= 15) {
            time = setInterval(() => setRefresh((prev) => prev + 1), 3000);
        }
        return () => clearInterval(time);
    }, []);

    useEffect(() => {
        if (scriptData?.scriptCode) {
            axiosInterceptorInstance
                .get(`/resources/option-chain?scripCode=${scriptData?.scriptCode}&segment=${scriptData.segment}`)
                .then((response) => {
                    let stockDetails = response.data;
                    setStockDetails(stockDetails);
                    setExpiryDate(Object.keys(stockDetails.expiryDates ?? {})?.[0]);
                    setSpotPrice(stockDetails.nseSymbol ? stockDetails.nseLtpPrice : stockDetails.bseLtpPrice);
                })
                .catch((error) => {
                    console.log(error);
                })
                .finally(() => setLoading(false));
        }
    }, [name, scriptData?.scriptCode]);

    const handleReload = useCallback(
        (isReload) => {
            if (stockDetails?.expiryDates) {
                isReload && setLoading(true);
                // axiosInterceptorInstance
                //     .get(
                //         `/resources/option-chain/options-chain-greeks?exchangeId=1&underlyingSId=${scripCode}&expiryDate=${stockDetails?.expiryDates?.[expiryDate]}&showFullChain=${showFullChain}&segment=${segment}`
                //     )
                //     .then((response) => {
                //         setData(response.data?.data?.data);
                //     })
                //     .finally(() => isReload && setLoading(false));

                axiosInterceptorInstance
                    .post(`https://livepub.univest.in/DataPub/api/SData/OptChainGeeks`, {
                        DoCompress: false,
                        Reserved: null,
                        UserId: "AJAYTEST",
                        UserType: null,
                        RequestCode: 188,
                        Count: 50,
                        Source: "W",
                        Data: {
                            UnderlyingSId: Math.abs(scriptData.scriptCode),
                            Exch: 1,
                            Exp: stockDetails?.expiryDates?.[expiryDate],
                            Count: 1,
                            Seg: scriptData.segment === "I" ? "0" : "1"
                        }
                    })
                    .then((response) => {
                        let d = response.data?.data;
                        setData({
                            ...d,
                            CE: d?.CE.sort((a, b) => a.Stk - b.Stk),
                            PE: d?.PE.sort((a, b) => a.Stk - b.Stk)
                        });
                    })
                    .finally(() => isReload && setLoading(false));
            }
        },
        [scriptData?.scriptCode, expiryDate, name, expiryDate]
    );

    useEffect(() => {
        handleReload(true);
    }, [stockDetails, expiryDate]);

    useEffect(() => {
        handleReload(false);
    }, [refresh]);

    useEffect(() => {
        if (!isLoading) {
            const closestStock = findClosestStock({ ...data, spotPrice });
            const elementId = closestStock?.Stk; // Ensure this is how you set ID in the DOM
            scrollInside(elementId, "option-chain-scrolling-container");
        }
    }, [isLoading]);

    const closestStock = useMemo(() => findClosestStock({ ...data, spotPrice: data?.SpotP }), [data]);
    const maxVolume = useMemo(() => findMaxVolume(data), [data]);

    return (
        <div className="p-4 h-full">
            {stockDetails?.nseSymbol || stockDetails?.bseSymbol ? (
                <div className="flex cursor-pointer items-center">
                    <WebSocket setSpotPrice={setSpotPrice} finCode={stockDetails?.finCode} />
                    <div className="mr-auto">
                        <div className="font-Inter text-xs md:text-base text-[#202020] font-bold flex items-center">
                            {stockDetails.nseSymbol ? stockDetails.nseSymbol : stockDetails.bseSymbol}
                            <div className="ml-2 px-3 border border-[#747474] rounded-full leading-[18px] text-[#747474] text-[10px] font-semibold">
                                Spot price
                            </div>
                        </div>
                        <div className="flex items-center gap-2 mt-1">
                            <LivePriceBox ltpPrice={spotPrice} textClass="text-[10px] md:text-sm font-bold" />
                            <NseOrBsePrice
                                nseLtpPrice={spotPrice}
                                nseClosePrice={
                                    stockDetails?.nseClosePrice > 0
                                        ? stockDetails?.nseClosePrice
                                        : stockDetails?.bseClosePrice
                                }
                                textClass="text-[10px] md:text-sm font-medium"
                            />
                        </div>
                    </div>
                    <div className="flex gap-1 lg:gap-3 items-center">
                        <div className="whitespace-nowrap text-[10px] md:text-sm font-semibold">Expiry Date</div>
                        <FnoTermDropDown
                            list={Object.keys(stockDetails?.expiryDates ?? {})?.map((ele) => ele)}
                            selected={expiryDate}
                            onChange={(e, i) => {
                                setExpiryDate(e);
                                const keysArray = Object.keys(stockDetails?.expiryDates);
                                Mixpanel.track("option_clicked", {
                                    page: "option_chain",
                                    symbol: stockDetails?.nseSymbol || stockDetails?.bseSymbol,
                                    expiry_date: e,
                                    instrument_name: getInstrument(stockDetails?.nseSymbol || stockDetails?.bseSymbol),
                                    expiry: "t" + (keysArray.indexOf(e) == 0 ? "" : "+" + keysArray.indexOf(e))
                                });
                            }}
                        />
                    </div>
                </div>
            ) : (
                <div className=" bg-[#F5F5F5] rounded-lg h-12"></div>
            )}
            <div
                className="border mt-6 border-[#F5F5F5] rounded-2xl h-[calc(100%-72px)] overflow-y-auto"
                id="option-chain-scrolling-container"
            >
                <div className="z-[11] sticky top-0 px-4 py-3 bg-[#F5F5F5] grid grid-cols-12 gap-1 items-center text-[10px] md:text-sm font-semibold w-full text-center">
                    <div className="grid grid-cols-5 col-span-5 gap-1">
                        <div className="col-span-1 ">Volume</div>
                        <div className="col-span-2 ">OI (OI Change)</div>
                        <div className="col-span-2 ">Call Price</div>
                    </div>
                    <div className="text-[10px] md:text-sm font-semibold w-full flex items-center col-span-2 gap-1">
                        <span className="mr-1 ml-auto text-[#414141]">Call OI</span>
                        <div className="w-6 h-2 rounded-full bg-[#EB4E2C]" />
                        <div className="w-6 h-2 rounded-full bg-[#26A649]" />
                        <span className="ml-1 mr-auto text-[#414141]">Put OI</span>
                    </div>
                    <div className="grid grid-cols-5 col-span-5 gap-1">
                        <div className="col-span-2 ">Put Price</div>
                        <div className="col-span-2 ">OI (OI Change)</div>
                        <div className="col-span-1 ">Volume</div>
                    </div>
                </div>
                {isLoading ? (
                    <div className="w-full h-full bg-slate-100 animate-pulse rounded-2xl flex justify-center">
                        <div className="self-center h-10 w-10 border-e-4 lg:h-20 lg:w-20 lg:border-e-8 border-[#00439D] animate-spin rounded-full" />
                    </div>
                ) : (
                    <div className="flex flex-col">
                        {[...Array(Math.max(data?.CE?.length ?? 0, data?.PE?.length ?? 0))].map((ele, index) => {
                            const CEData = data?.CE?.[index];
                            const PEData = data?.PE?.[index];
                            if (CEData?.OI > 0 && PEData?.OI > 0) {
                                return (
                                    <OptionChainGrid
                                        key={index}
                                        ele={ele}
                                        CE={CEData}
                                        PE={PEData}
                                        closestStock={closestStock}
                                        maxVolume={maxVolume}
                                        SpotPrice={data?.SpotP}
                                        stockDetails={stockDetails}
                                        expiryDate={expiryDate}
                                    />
                                );
                            }
                        })}
                    </div>
                )}
            </div>
        </div>
    );
}
export function WebSocket({ finCode, setSpotPrice }) {
    useSubscription(`/stock/live-price/${finCode}`, (res) => setSpotPrice(JSON.parse(res.body)?.ltpPrice));
    return;
}

const OptionChainGrid = memo(({ CE, PE, closestStock, maxVolume, SpotPrice, stockDetails, expiryDate }) => {
    const router = useRouter();
    const [hoveredSide, setHoveredSide] = useState(null); // 'CE' or 'PE' or null
    const [hoveredFinCode, setHoveredFinCode] = useState(null);
    const [isLoadingFinCode, setIsLoadingFinCode] = useState(false);

    // Function to fetch finCode only when needed
    const fetchFinCode = useCallback((scriptCode, side) => {
        setIsLoadingFinCode(true);
        setHoveredSide(side);

        axiosInterceptorInstance
            .get(`/resources/stock-details/fincode-from-scripcode?scripCode=${scriptCode}`)
            .then((res) => {
                setHoveredFinCode(res?.data?.finCode);
            })
            .catch((error) => {
                console.log(`Error fetching ${side} finCode:`, error);
            })
            .finally(() => {
                setIsLoadingFinCode(false);
            });
    }, []);

    // Clear finCode when mouse leaves the area
    const clearHoveredData = () => {
        setHoveredSide(null);
        setHoveredFinCode(null);
    };

    return (
        <>
            {closestStock?.SId == CE?.SId && (
                <div className="font-Inter z-10 flex justify-center border-y-2 border-[#FFE0AD] text-[10px] md:text-sm font-medium py-3 sticky top-11 bottom-0 bg-white">
                    SPOT : <span className="ml-2 font-bold">{putComma(SpotPrice)}</span>
                </div>
            )}
            <div
                onClick={() => {
                    Mixpanel.track("stock_clicked", {
                        page: "option_chain",
                        symbol: stockDetails?.nseSymbol || stockDetails?.bseSymbol,
                        expiry_date: expiryDate,
                        instrument_name: getInstrument(stockDetails?.nseSymbol || stockDetails?.bseSymbol)
                    });
                }}
                id={CE?.Stk}
                className={`font-Inter border-b border-[#F5F5F5] grid grid-cols-12 gap-1 text-[10px] md:text-sm font-semibold text-center pt-2 pb-2.5 px-3 transition-all relative bg-white`}
            >
                <div
                    className={`ease-in-out duration-300 rounded-s absolute top-0 bottom-0 bg-[#FFF9F0] z-0 
                ${
                    parseFloat(closestStock?.Stk ?? "0") > parseFloat(CE?.Stk) ? "right-1/2 left-0" : "right-0 left-1/2"
                }`}
                />
                <div className="flex absolute bottom-1 left-0 right-0 gap-1">
                    <div className="w-full flex justify-end">
                        <div
                            className="h-[3px] min-w-1 rounded-full bg-[#EB4E2C]"
                            style={{ width: `${(CE?.Vol * 90) / maxVolume}%` }}
                        />
                    </div>
                    <div className="w-full flex justify-start">
                        <div
                            className="h-[3px] min-w-1 rounded-full bg-[#26A649]"
                            style={{ width: `${(PE?.Vol * 90) / maxVolume}%` }}
                        />
                    </div>
                </div>
                <div
                    className="grid grid-cols-5 col-span-5 gap-1 group relative"
                    onMouseEnter={() => fetchFinCode(CE?.SId, "CE")}
                    onMouseLeave={clearHoveredData}
                >
                    <div className="z-[1] mt-1.5 col-span-1">{Actions.trimNumber(CE?.Vol, 0)}</div>
                    <div className="col-span-2 flex flex-col items-center z-[1] mt-1.5 transition-all">
                        {Actions.trimNumber(CE?.OI, 0)}
                        <br />
                        <span className={cn(CE?.OIChng >= 0 ? "text-[#26A649]" : "text-[#EB4E2C]", "text-xs")}>
                            {Actions.trimNumber(CE?.OIChng)} ({putComma(CE?.OIPerChng)}%)
                        </span>
                    </div>
                    <div className="col-span-2 flex flex-col items-center z-[1] mt-1.5 transition-all group-hover:opacity-0">
                        {putComma(CE?.Ltp)}
                        <br />
                        <span className={cn(CE?.C >= 0 ? "text-[#26A649]" : "text-[#EB4E2C]", "text-xs")}>
                            {putComma(CE?.C)} ({putComma(CE?.PC)}%)
                        </span>
                    </div>
                    {hoveredSide === "CE" && (
                        <OnHoverOption
                            isOption={true}
                            finCode={hoveredFinCode}
                            nseSymbol={CE?.Sym}
                            exchange={stockDetails?.nseSymbol ? "NSE" : "BSE"}
                            source={"option_chain"}
                            onClickTradingView={() => {
                                if (hoveredFinCode) {
                                    router.push(
                                        Actions.toFNODetail(`${stockDetails?.finCode}-${hoveredFinCode}`, "/charts")
                                    );
                                }
                            }}
                        />
                    )}
                </div>
                <div className="bg-[#FFE0AD] text-center py-3 text-[10px] md:text-sm font-semibold rounded-md z-[1] col-span-2">
                    {putComma(parseInt(CE?.Stk ?? "0"), 0)}
                </div>
                <div
                    className="grid grid-cols-5 col-span-5 gap-1 group relative"
                    onMouseEnter={() => fetchFinCode(PE?.SId, "PE")}
                    onMouseLeave={clearHoveredData}
                >
                    <div className="col-span-2 flex flex-col items-center z-[1] mt-1.5 transition-all group-hover:opacity-0">
                        {putComma(PE?.Ltp)}
                        <br />
                        <span className={cn(PE?.C >= 0 ? "text-[rgb(38,166,73)]" : "text-[#EB4E2C]", "text-xs")}>
                            {putComma(PE?.C)} ({putComma(PE?.PC)}%)
                        </span>
                    </div>
                    <div className="col-span-2 flex flex-col items-center z-[1] mt-1.5">
                        {Actions.trimNumber(PE?.OI, 0)}
                        <br />
                        <span className={cn(PE?.OIChng >= 0 ? "text-[#26A649]" : "text-[#EB4E2C]", "text-xs")}>
                            {Actions.trimNumber(PE?.OIChng)} ({putComma(PE?.OIPerChng)}%)
                        </span>
                    </div>
                    <div className="col-span-1 z-[1] mt-1.5">{Actions.trimNumber(PE?.Vol, 0)}</div>
                    {hoveredSide === "PE" && (
                        <OnHoverOption
                            isOption={true}
                            finCode={hoveredFinCode}
                            nseSymbol={PE?.Sym}
                            exchange={stockDetails?.nseSymbol ? "NSE" : "BSE"}
                            className="left-0 group-hover:left-4"
                            onClickTradingView={() => {
                                if (hoveredFinCode) {
                                    router.push(
                                        Actions.toFNODetail(`${stockDetails?.finCode}-${hoveredFinCode}`, "/charts")
                                    );
                                }
                            }}
                        />
                    )}
                </div>
            </div>
        </>
    );
});

OptionChainGrid.displayName = "OptionChainGrid";
